'use client';

import { Maybe, Page } from '@/tina/__generated__/types';
import * as stylex from '@stylexjs/stylex';
import { tinaField } from 'tinacms/dist/react';
import { FormButton } from '../HubspotForm/FormButton';
import { MeetingButton } from '../HubspotMeetingLink/MeetingButton';
import { LinkButton } from './LinkButton';
import { Button, ButtonProps } from './ButtonComponent';
import { sendGTMEvent } from '@next/third-parties/google';
import { NextLinkWithEventPayload } from '../NextLinkWithEventPayload';
import { Dialog } from '@base-ui/react';
import { SteppedFormHandle } from '../SteppedForm/dialogHandle';
import { Suspense } from 'react';
export type ActionArgs = {
  componentName?: string;
  buttonVariant?: 'primary' | 'secondary' | 'ghost';
  iconLeft?: string;
  iconRight?: string;
  buttonText?: string;
  size?: ButtonProps['size'];
  ctaType?: 'topLevelPageLink' | 'externalLink' | 'hubspotForm' | 'hubspotMeeting';
  url?: string;
  formId?: string;
  meetingLink?: string;
};
export interface ActionButtonsProps {
  actions: Maybe<Action>[];
  styles?: stylex.StyleXStyles;
  size?: ButtonProps['size'];
  args?: ActionArgs;
}
export type Action = TopLevelPageLinkAction | FormAction | MeetingAction | ExternalLinkAction | ConversionFormAction;
interface ConversionFormAction extends ButtonAction {
  ctaType?: 'conversionForm';
}
interface TopLevelPageLinkAction extends ButtonAction {
  ctaType?: 'topLevelPageLink';
  topLevelPageReference: Page;
}
interface ExternalLinkAction extends ButtonAction {
  ctaType?: 'externalLink';
  externalLink: string;
}
interface FormAction extends ButtonAction {
  ctaType?: 'hubspotForm';
  hubspotForm?: {
    formId?: string;
  };
}
interface MeetingAction extends ButtonAction {
  ctaType?: 'hubspotMeeting';
  meetingLink?: {
    meetingLink?: string;
  };
}
interface ButtonAction {
  buttonVariant?: 'primary' | 'secondary' | 'ghost';
  iconLeft?: string;
  iconRight?: string;
  buttonText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}
export function ActionButtons({
  actions,
  styles,
  size,
  args = {}
}: ActionButtonsProps) {
  return <>
      {actions?.map((action, i) => {
      if (action && action.ctaType) {
        const actionArgs = {
          componentName: args.componentName || 'unspecified',
          buttonVariant: action.buttonVariant || 'primary',
          iconLeft: action.iconLeft || 'none',
          iconRight: action.iconRight || 'none',
          buttonText: action.buttonText || 'none',
          size: args.size || 'regular',
          ctaType: action.ctaType || 'unspecified',
          url: action.externalLink || 'none',
          formId: action.hubspotForm?.formId || 'none',
          meetingLink: action.meetingLink?.meetingLink || 'none'
        };
        const buttonLabel = action.buttonText;
        const gtmPayload = {
          event: 'cta_click',
          eventCategory: 'cta',
          eventAction: 'click',
          eventLabel: buttonLabel,
          args: actionArgs
        };
        function onClick(e?: React.MouseEvent) {
          sendGTMEvent({
            ...gtmPayload,
            mouseEvent: e
          });
        }
        const {
          ctaType
        } = action;
        const key = `${i}-${ctaType}`;
        switch (ctaType) {
          case 'hubspotMeeting':
            return <MeetingButton key={key} buttonProps={{
              theme: action.buttonVariant as 'primary' | 'secondary',
              'data-tina-field': tinaField(action, 'buttonText'),
              styles,
              iconLeft: action.iconLeft,
              iconRight: action.iconRight,
              centerTextBlock: true,
              size
            }} meetingLink={action.meetingLink?.meetingLink as string} onClick={onClick}>
                  {action.buttonText}
                </MeetingButton>;
          case 'hubspotForm':
            return <FormButton key={key} buttonProps={{
              theme: action.buttonVariant as 'primary' | 'secondary',
              'data-tina-field': tinaField(action, 'buttonText'),
              styles,
              centerTextBlock: true,
              iconLeft: action.iconLeft,
              iconRight: action.iconRight,
              size
            }} formId={action.hubspotForm?.formId} onClick={onClick}>
                  {buttonLabel}
                </FormButton>;
          case 'conversionForm':
            return <Dialog.Trigger handle={SteppedFormHandle} key={key} render={<Button theme={action.buttonVariant as 'primary' | 'secondary'} size={size} styles={styles} data-tina-field={tinaField(action, 'buttonText')} centerTextBlock iconLeft={action.iconLeft} iconRight={action.iconRight} />}>
                  {buttonLabel}
                </Dialog.Trigger>;
          case 'topLevelPageLink':
            return <LinkButton href={`/${action.topLevelPageReference?._sys?.breadcrumbs.join('/')}`} variant="link" iconLeft={action.iconLeft} key={key} styles={styles} data-tina-field={tinaField(action, 'buttonText')} centerTextBlock size={size} clickEventPayload={gtmPayload}>
                  {action.buttonText}
                </LinkButton>;
          case 'externalLink':
            return <Button asChild variant="link" iconLeft={action.iconLeft} iconRight={action.iconRight} key={key} styles={styles} data-tina-field={tinaField(action, 'buttonText')} centerTextBlock size={size}>
                  <NextLinkWithEventPayload href={action.externalLink} clickEventPayload={gtmPayload}>
                    {action.buttonText}
                  </NextLinkWithEventPayload>
                </Button>;
          default:
            return null;
        }
      }
      return null;
    })}
    </>;
}